import { useState, useMemo, useId } from 'react';
import {
  Download,
  FileText,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { cn } from '@/lib/utils';
import { toCsv, downloadCsv, type CsvColumn } from '@/lib/csv';
import {
  useDailyPickup,
  useMonthlyRevenue,
  useChannelPerformance,
  usePropertyPerformance,
} from '@/hooks/api/useReports';

// ---- Constants ----

type TabId = 'daily-pickup' | 'monthly-revenue' | 'channel-performance' | 'property-performance';

const TABS: { id: TabId; label: string }[] = [
  { id: 'daily-pickup', label: 'Daily Pickup' },
  { id: 'monthly-revenue', label: 'Monthly Revenue' },
  { id: 'channel-performance', label: 'Channel Performance' },
  { id: 'property-performance', label: 'Property Performance' },
];

const CHANNEL_COLORS = {
  airbnb: '#FF5A5F',
  bookingCom: '#003580',
  expedia: '#FFC72C',
  direct: '#10B981',
};

// ---- Helpers ----

function fmtCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function fmtNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function showToast(message: string) {
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.className = 'fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg bg-gray-900 text-white text-sm font-medium';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity 0.3s'; setTimeout(() => el.remove(), 300); }, 2500);
}

// ---- Shared Components ----

function DateRangeSelector({ fromDate, toDate, onFromChange, onToChange }: { fromDate: string; toDate: string; onFromChange: (v: string) => void; onToChange: (v: string) => void }) {
  const fromId = useId();
  const toId = useId();
  return (
    <div className="flex items-end gap-3 flex-wrap">
      <div>
        <label htmlFor={fromId} className="block text-xs font-medium text-gray-600 mb-1">From</label>
        <input id={fromId} type="date" value={fromDate} onChange={(e) => onFromChange(e.target.value)} className={cn('rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
      </div>
      <div>
        <label htmlFor={toId} className="block text-xs font-medium text-gray-600 mb-1">To</label>
        <input id={toId} type="date" value={toDate} onChange={(e) => onToChange(e.target.value)} className={cn('rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
      </div>
    </div>
  );
}

function ExportButtons({ label, onExportCsv }: { label: string; onExportCsv?: (() => void) | undefined }) {
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => (onExportCsv ? onExportCsv() : showToast(`${label} CSV export started.`))} className={cn('inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors', 'border-gray-300 text-gray-600 hover:bg-gray-50', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500')} aria-label={`Export ${label} as CSV`}>
        <Download size={14} aria-hidden="true" />Export CSV
      </button>
      <button type="button" onClick={() => showToast(`${label} PDF export started.`)} className={cn('inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors', 'border-gray-300 text-gray-600 hover:bg-gray-50', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500')} aria-label={`Export ${label} as PDF`}>
        <FileText size={14} aria-hidden="true" />Export PDF
      </button>
    </div>
  );
}

/** Build a CSV from report rows/columns and download it with a dated filename. */
function exportReportCsv(label: string, rows: Array<Record<string, unknown>>, columns: CsvColumn[]) {
  const csv = toCsv(rows, columns);
  const slug = label.toLowerCase().replace(/\s+/g, '-');
  downloadCsv(`${slug}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
}

function TabControlsRow({ fromDate, toDate, onFromChange, onToChange, exportLabel, onExportCsv }: { fromDate: string; toDate: string; onFromChange: (v: string) => void; onToChange: (v: string) => void; exportLabel: string; onExportCsv?: (() => void) | undefined }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <DateRangeSelector fromDate={fromDate} toDate={toDate} onFromChange={onFromChange} onToChange={onToChange} />
      <ExportButtons label={exportLabel} onExportCsv={onExportCsv} />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
      <span className="ml-2 text-sm text-gray-500">Loading report data...</span>
    </div>
  );
}

// ---- Tab 1: Daily Pickup ----

function DailyPickupTab() {
  const [fromDate, setFromDate] = useState('2026-05-29');
  const [toDate, setToDate] = useState('2026-07-16');
  const { data, isLoading } = useDailyPickup({ date: fromDate, to: toDate });

  const rows: Array<Record<string, unknown>> = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ?? []);
  }, [data]);

  const totals = useMemo(() => rows.reduce<{ newBookings: number; airbnb: number; bookingCom: number; expedia: number; direct: number; revenue: number }>(
    (acc, row) => ({
      newBookings: acc.newBookings + ((row.newBookings as number) ?? 0),
      airbnb: acc.airbnb + ((row.airbnb as number) ?? 0),
      bookingCom: acc.bookingCom + ((row.bookingCom as number) ?? 0),
      expedia: acc.expedia + ((row.expedia as number) ?? 0),
      direct: acc.direct + ((row.direct as number) ?? 0),
      revenue: acc.revenue + ((row.revenue as number) ?? 0),
    }),
    { newBookings: 0, airbnb: 0, bookingCom: 0, expedia: 0, direct: 0, revenue: 0 },
  ), [rows]);

  function handleExportCsv() {
    const exportRows = rows.map((row) => ({
      date: (row.label as string) ?? (row.date as string) ?? '',
      newBookings: (row.newBookings as number) ?? 0,
      airbnb: (row.airbnb as number) ?? 0,
      bookingCom: (row.bookingCom as number) ?? 0,
      expedia: (row.expedia as number) ?? 0,
      direct: (row.direct as number) ?? 0,
      revenue: (row.revenue as number) ?? 0,
    }));
    exportReportCsv('Daily Pickup', exportRows, [
      { key: 'date', header: 'Date' },
      { key: 'newBookings', header: 'New Bookings' },
      { key: 'airbnb', header: 'Airbnb' },
      { key: 'bookingCom', header: 'Booking.com' },
      { key: 'expedia', header: 'Expedia' },
      { key: 'direct', header: 'Direct' },
      { key: 'revenue', header: 'Revenue' },
    ]);
  }

  if (isLoading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <TabControlsRow fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} exportLabel="Daily Pickup" onExportCsv={handleExportCsv} />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100" aria-label="Daily pickup report">
            <thead>
              <tr className="bg-gray-50">
                {['Date', 'New Bookings', 'Airbnb', 'Booking.com', 'Expedia', 'Direct', 'Revenue'].map((col) => (
                  <th key={col} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={(row.date as string) ?? ''} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{(row.label as string) ?? (row.date as string) ?? ''}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{(row.newBookings as number) ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(row.airbnb as number) ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(row.bookingCom as number) ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(row.expedia as number) ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(row.direct as number) ?? 0}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fmtCurrency((row.revenue as number) ?? 0)}</td>
                </tr>
              ))}
              <tr className="bg-primary-50 font-semibold">
                <td className="px-4 py-3 text-sm text-primary-900">Total</td>
                <td className="px-4 py-3 text-sm text-primary-900">{totals.newBookings}</td>
                <td className="px-4 py-3 text-sm text-primary-800">{totals.airbnb}</td>
                <td className="px-4 py-3 text-sm text-primary-800">{totals.bookingCom}</td>
                <td className="px-4 py-3 text-sm text-primary-800">{totals.expedia}</td>
                <td className="px-4 py-3 text-sm text-primary-800">{totals.direct}</td>
                <td className="px-4 py-3 text-sm text-primary-900">{fmtCurrency(totals.revenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Tab 2: Monthly Revenue ----

function MonthlyRevenueTab() {
  const [fromDate, setFromDate] = useState('2025-10-01');
  const [toDate, setToDate] = useState('2026-03-31');
  const { data, isLoading } = useMonthlyRevenue();

  const rows: Array<Record<string, unknown>> = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ?? []);
  }, [data]);

  function handleExportCsv() {
    const exportRows = rows.map((row) => ({
      month: (row.month as string) ?? '',
      totalRevenue: (row.totalRevenue as number) ?? 0,
      bookings: (row.bookings as number) ?? 0,
      adr: (row.adr as number) ?? 0,
      occupancyPercent: (row.occupancyPercent as number) ?? 0,
    }));
    exportReportCsv('Monthly Revenue', exportRows, [
      { key: 'month', header: 'Month' },
      { key: 'totalRevenue', header: 'Total Revenue' },
      { key: 'bookings', header: 'Bookings' },
      { key: 'adr', header: 'ADR' },
      { key: 'occupancyPercent', header: 'Occupancy %' },
    ]);
  }

  if (isLoading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <TabControlsRow fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} exportLabel="Monthly Revenue" onExportCsv={handleExportCsv} />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Revenue by Channel</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={rows} margin={{ top: 5, right: 16, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(value: number, name: string) => [fmtCurrency(value), name]} contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="rect" iconSize={10} />
            <Bar dataKey="airbnb" name="Airbnb" stackId="revenue" fill={CHANNEL_COLORS.airbnb} />
            <Bar dataKey="bookingCom" name="Booking.com" stackId="revenue" fill={CHANNEL_COLORS.bookingCom} />
            <Bar dataKey="expedia" name="Expedia" stackId="revenue" fill={CHANNEL_COLORS.expedia} />
            <Bar dataKey="direct" name="Direct" stackId="revenue" fill={CHANNEL_COLORS.direct} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100" aria-label="Monthly revenue summary">
            <thead>
              <tr className="bg-gray-50">
                {['Month', 'Total Revenue', 'Bookings', 'ADR', 'Occupancy %'].map((col) => (
                  <th key={col} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={(row.month as string) ?? ''} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.month as string}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fmtCurrency((row.totalRevenue as number) ?? 0)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtNumber((row.bookings as number) ?? 0)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtCurrency((row.adr as number) ?? 0)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{(row.occupancyPercent as number) ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Tab 3: Channel Performance ----

function ChannelPerformanceTab() {
  const [fromDate, setFromDate] = useState('2026-05-01');
  const [toDate, setToDate] = useState('2026-07-31');
  const { data, isLoading } = useChannelPerformance({ from: fromDate, to: toDate });

  const reportData: Record<string, unknown> = useMemo(() => {
    if (!data) return {};
    return data;
  }, [data]);

  const channels: Array<Record<string, unknown>> = useMemo(() => {
    if (Array.isArray(reportData)) return reportData;
    return Array.isArray(reportData.channels) ? reportData.channels : (Array.isArray(reportData.data) ? reportData.data : []);
  }, [reportData]);

  const trends: Array<Record<string, unknown>> = useMemo(() => {
    return Array.isArray(reportData.trends) ? reportData.trends : [];
  }, [reportData]);

  function handleExportCsv() {
    const exportRows = channels.map((ch) => ({
      channel: (ch.channel as string) ?? '',
      revenue: (ch.revenue as number) ?? 0,
      bookings: (ch.bookings as number) ?? 0,
      adr: (ch.adr as number) ?? 0,
      avgLeadTimeDays: (ch.avgLeadTimeDays as number) ?? 0,
      cancellationRate: (ch.cancellationRate as number) ?? 0,
    }));
    exportReportCsv('Channel Performance', exportRows, [
      { key: 'channel', header: 'Channel' },
      { key: 'revenue', header: 'Revenue' },
      { key: 'bookings', header: 'Bookings' },
      { key: 'adr', header: 'ADR' },
      { key: 'avgLeadTimeDays', header: 'Avg Lead Time (days)' },
      { key: 'cancellationRate', header: 'Cancellation Rate %' },
    ]);
  }

  if (isLoading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <TabControlsRow fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} exportLabel="Channel Performance" onExportCsv={handleExportCsv} />
      <section aria-label="Channel comparison cards">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {channels.map((ch) => (
            <div key={ch.channel as string} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col gap-3" aria-label={`${ch.channel as string} performance`}>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: (ch.color as string) ?? '#6366F1' }} aria-hidden="true" />
                <h4 className="text-sm font-semibold text-gray-900">{ch.channel as string}</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-gray-500">Revenue</p><p className="text-lg font-bold text-gray-900">{fmtCurrency((ch.revenue as number) ?? 0)}</p></div>
                <div><p className="text-xs text-gray-500">Bookings</p><p className="text-lg font-bold text-gray-900">{fmtNumber((ch.bookings as number) ?? 0)}</p></div>
                <div><p className="text-xs text-gray-500">ADR</p><p className="text-lg font-bold text-gray-900">{fmtCurrency((ch.adr as number) ?? 0)}</p></div>
                <div><p className="text-xs text-gray-500">Commission</p><p className="text-lg font-bold text-gray-900">{(ch.commissionRate as number) ?? 0}%</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {trends.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Channel Revenue Over Time</h3>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={trends} margin={{ top: 5, right: 16, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: number, name: string) => [fmtCurrency(value), name]} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" iconSize={8} />
              <Line type="monotone" dataKey="airbnb" name="Airbnb" stroke={CHANNEL_COLORS.airbnb} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="bookingCom" name="Booking.com" stroke={CHANNEL_COLORS.bookingCom} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="expedia" name="Expedia" stroke={CHANNEL_COLORS.expedia} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="direct" name="Direct" stroke={CHANNEL_COLORS.direct} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100" aria-label="Channel performance details">
            <thead>
              <tr className="bg-gray-50">
                {['Channel', 'Revenue', 'Bookings', 'ADR', 'Avg Lead Time', 'Cancellation Rate'].map((col) => (
                  <th key={col} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {channels.map((ch) => (
                <tr key={ch.channel as string} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: (ch.color as string) ?? '#6366F1' }} aria-hidden="true" />
                      <span className="font-medium text-gray-900">{ch.channel as string}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fmtCurrency((ch.revenue as number) ?? 0)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtNumber((ch.bookings as number) ?? 0)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtCurrency((ch.adr as number) ?? 0)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{(ch.avgLeadTimeDays as number) ?? 0} days</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{(ch.cancellationRate as number) ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Tab 4: Property Performance ----

function PropertyPerformanceTab() {
  const [fromDate, setFromDate] = useState('2026-05-01');
  const [toDate, setToDate] = useState('2026-07-31');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const selectId = useId();

  const { data, isLoading } = usePropertyPerformance({
    propertyId: selectedPropertyId || undefined,
    from: fromDate,
    to: toDate,
  });

  const properties: Array<Record<string, unknown>> = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ? (Array.isArray(data.data) ? data.data : [data.data]) : [data]);
  }, [data]);

  const property: Record<string, unknown> = useMemo(() => {
    if (selectedPropertyId) return properties.find((p) => p.propertyId === selectedPropertyId) ?? properties[0] ?? {};
    return properties[0] ?? {};
  }, [properties, selectedPropertyId]);

  // Set initial selected property
  useMemo(() => {
    if (!selectedPropertyId && properties.length > 0 && properties[0].propertyId) {
      setSelectedPropertyId(properties[0].propertyId as string);
    }
  }, [properties, selectedPropertyId]);

  const monthlyRevenue: Array<Record<string, unknown>> = Array.isArray(property.monthlyRevenue) ? property.monthlyRevenue : [];
  const propUnits: Array<Record<string, unknown>> = Array.isArray(property.units) ? property.units : [];

  function handleExportCsv() {
    const propertyName = (property.propertyName as string) ?? '';
    if (propUnits.length > 0) {
      const exportRows = propUnits.map((unit) => ({
        propertyName,
        unitName: (unit.unitName as string) ?? '',
        bookings: (unit.bookings as number) ?? 0,
        revenue: (unit.revenue as number) ?? 0,
        occupancyPercent: (unit.occupancyPercent as number) ?? 0,
        adr: (unit.adr as number) ?? 0,
      }));
      exportReportCsv('Property Performance', exportRows, [
        { key: 'propertyName', header: 'Property' },
        { key: 'unitName', header: 'Unit Name' },
        { key: 'bookings', header: 'Bookings' },
        { key: 'revenue', header: 'Revenue' },
        { key: 'occupancyPercent', header: 'Occupancy %' },
        { key: 'adr', header: 'ADR' },
      ]);
      return;
    }
    // No unit breakdown available — export the property-level KPI summary shown.
    const summaryRow = {
      propertyName,
      revenue: (property.revenue as number) ?? 0,
      occupancyPercent: (property.occupancyPercent as number) ?? 0,
      adr: (property.adr as number) ?? 0,
      revpar: (property.revpar as number) ?? 0,
    };
    exportReportCsv('Property Performance', [summaryRow], [
      { key: 'propertyName', header: 'Property' },
      { key: 'revenue', header: 'Revenue' },
      { key: 'occupancyPercent', header: 'Occupancy %' },
      { key: 'adr', header: 'ADR' },
      { key: 'revpar', header: 'RevPAR' },
    ]);
  }

  if (isLoading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label htmlFor={selectId} className="block text-xs font-medium text-gray-600 mb-1">Property</label>
            <div className="relative">
              <select id={selectId} value={selectedPropertyId} onChange={(e) => setSelectedPropertyId(e.target.value)} className={cn('appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')}>
                {properties.map((p) => (<option key={p.propertyId as string} value={p.propertyId as string}>{p.propertyName as string}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <DateRangeSelector fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} />
        </div>
        <ExportButtons label="Property Performance" onExportCsv={handleExportCsv} />
      </div>

      <section aria-label="Property KPIs">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Revenue', value: fmtCurrency((property.revenue as number) ?? 0) },
            { label: 'Occupancy', value: `${(property.occupancyPercent as number) ?? 0}%` },
            { label: 'ADR', value: fmtCurrency((property.adr as number) ?? 0) },
            { label: 'RevPAR', value: fmtCurrency((property.revpar as number) ?? 0) },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" aria-label={`${kpi.label}: ${kpi.value}`}>
              <p className="text-xs text-gray-500 mb-1">{kpi.label}</p>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      </section>

      {monthlyRevenue.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Monthly Revenue - {(property.propertyName as string) ?? ''}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyRevenue} margin={{ top: 5, right: 16, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: number) => [fmtCurrency(value), 'Revenue']} contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {propUnits.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-800">Unit Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label={`Unit breakdown for ${(property.propertyName as string) ?? ''}`}>
              <thead>
                <tr className="bg-gray-50">
                  {['Unit Name', 'Bookings', 'Revenue', 'Occupancy %', 'ADR'].map((col) => (
                    <th key={col} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {propUnits.map((unit) => (
                  <tr key={unit.unitName as string} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{unit.unitName as string}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtNumber((unit.bookings as number) ?? 0)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fmtCurrency((unit.revenue as number) ?? 0)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{(unit.occupancyPercent as number) ?? 0}%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtCurrency((unit.adr as number) ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Page ----

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('daily-pickup');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-0">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reports &amp; Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Financial and operational reporting</p>
        </div>
      </div>

      <nav aria-label="Report tabs">
        <div className="flex gap-1 border-b border-gray-200" role="tablist">
          {TABS.map((tab) => (
            <button key={tab.id} role="tab" id={`tab-${tab.id}`} aria-selected={activeTab === tab.id} aria-controls={`panel-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={cn('px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors rounded-t-lg', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500', activeTab === tab.id ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')}>
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <div role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
        {activeTab === 'daily-pickup' && <DailyPickupTab />}
        {activeTab === 'monthly-revenue' && <MonthlyRevenueTab />}
        {activeTab === 'channel-performance' && <ChannelPerformanceTab />}
        {activeTab === 'property-performance' && <PropertyPerformanceTab />}
      </div>
    </div>
  );
}
